import React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from '@core-ui/Components/ui/avatar';
import EmptyState from '../shared/EmptyState';
import { sk } from '../shared/skeletonClass';
import type { SdrRanking } from '@types';

interface SdrLeaderboardProps {
  data: SdrRanking[];
  isLoading: boolean;
}

function getInitials (name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

const RANK_COLORS = [
  'bg-amber-500',
  'bg-slate-400',
  'bg-orange-600',
  'bg-muted-foreground',
  'bg-muted-foreground',
];

export default function SdrLeaderboard ({ data, isLoading }: SdrLeaderboardProps): React.JSX.Element {
  const { t } = useTranslation();
  const maxAppointments = data.length > 0 ? Math.max(...data.map((s) => s.appointments)) : 1;

  if (isLoading) {
    return (
      <div className="rounded-xl border border-border bg-card">
        <div className="flex items-start justify-between p-5 pb-3">
          <div>
            <div className={`h-5 w-36 mb-1.5 ${sk}`} />
            <div className={`h-3 w-52 ${sk}`} />
          </div>
          <div className={`h-7 w-24 ${sk}`} />
        </div>
        <div className="px-5 pb-5 space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className={`w-4 h-4 ${sk}`} />
              <div className={`h-7 w-7 rounded-full ${sk}`} />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1.5">
                  <div className={`h-4 w-28 ${sk}`} />
                  <div className={`h-4 w-6 ${sk}`} />
                </div>
                <div className={`h-1.5 w-full rounded-full ${sk}`} />
                <div className="flex items-center justify-between mt-1.5">
                  <div className={`h-3 w-14 ${sk}`} />
                  <div className={`h-3 w-20 ${sk}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="flex items-start justify-between p-5 pb-3">
        <div>
          <h3 className="text-base font-semibold text-foreground">{t('dashboard.overview.leaderboard.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('dashboard.overview.leaderboard.description')}</p>
        </div>
        <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-md border border-border">
          {t('dashboard.overview.leaderboard.appointments')}
        </span>
      </div>
      <div className="px-5 pb-5 space-y-3 max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:rounded-full">
        {data.length === 0 && (
          <EmptyState message={t('dashboard.overview.leaderboard.empty')} />
        )}
        {data.map((sdr, index) => (
          <div key={sdr.sdrId} className="flex items-center gap-3">
            {/* Rank */}
            <span className="text-sm font-medium text-muted-foreground w-4 text-right tabular-nums">
              {index + 1}
            </span>

            {/* Avatar */}
            <Avatar className="h-7 w-7">
              {sdr.avatar !== null && <AvatarImage src={sdr.avatar} />}
              <AvatarFallback className={`text-[10px] text-white ${RANK_COLORS[index] ?? 'bg-muted-foreground'}`}>
                {getInitials(sdr.name)}
              </AvatarFallback>
            </Avatar>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-foreground truncate">{sdr.name}</span>
                <span className="text-sm font-bold tabular-nums text-foreground ml-2">{sdr.appointments}</span>
              </div>
              {/* Progress bar */}
              <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${(sdr.appointments / maxAppointments) * 100}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[11px] text-muted-foreground">{t('dashboard.overview.leaderboard.calls', { count: sdr.calls })}</span>
                <span className="text-[11px] text-muted-foreground">{t('dashboard.overview.leaderboard.connect', { value: sdr.connectRate })}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
